import { ArrowRight, Check, Mail, MailCheck, RefreshCwOff } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import Modal, { ModalComponentProps } from "../generic/Modal";
import { ToasterContext } from "../../contexts/toaster.context";
import useForm from "../../hooks/form.hook";
import useObjectState from "../../hooks/object-state.hook";
import { isBlank, passwordRegexTests } from "../../util/base.util";
import { Input } from "../generic/Input";
import { AuthContext } from "../../contexts/auth.context";

export default function VerifyEmailModal(props: ModalComponentProps) {
    const toaster = useContext(ToasterContext);
    const auth = useContext(AuthContext);

    const registerForm = useForm({
        email: auth.user!.email
    });

    const codeForm = useForm({
        code: ""
    });

    const [state, updateState] = useObjectState({
        bCodeSent: false,
        timer: 0
    });

    const [page, setPage] = useState(0);

    async function sendCode() {
        codeForm.clearErrors();
        registerForm.clearErrors();

        if (isBlank(registerForm.state.email)) {
            registerForm.setErrors({email: "Cannot be blank"});
            return;
        }

        const res = await codeForm.submit("/auth/send-pre-email-verification", {
            body: {
                email: registerForm.state.email
            },
            onError: registerForm.setErrors,
            onSuccess: (data) => {
                updateState({bCodeSent: true, timer: 60});
            }
        });
    }

    async function verifyCode() {
        codeForm.clearErrors();
        registerForm.clearErrors();

        const res = await registerForm.submit("/auth/verify-email-code", {
            body: {
                email: registerForm.state.email,
                code: codeForm.state.code
            },
            onError: codeForm.setErrors,
            onSuccess: () => {
                setPage(1);
            }
        });
    }

    const isBusy = useMemo(() => {
        return registerForm.isLoading || codeForm.isLoading;
    }, [registerForm.isLoading, codeForm.isLoading]);

    useEffect(() => {
        if (state.timer > 0) {
            const a = setTimeout(() => {
                updateState({timer: state.timer - 1});
            }, 998);

            return () => {
                clearTimeout(a);
            }
        }
    }, [state.timer]);

    return (
        <Modal {...props} title="Verify your email address">
            {page === 1 && (
                <div className="text-center">
                    <Check className="mx-auto text-success" size={36} />
                    <p>Email verified successfully</p>

                    <p>Thank you</p>

                    <div>
                        <button onClick={() => {
                            if (auth.user) auth.setUser({...auth.user, emailVerified: new Date().toISOString()});
                            props.onClose?.();
                        }} className="btn w-full">Close</button>
                    </div>
                </div>
            )}

            {page === 0 && (
                <div className="grid gap-4 mt-4">
                    <Input readOnly disabled name="email" value={registerForm.state.email}
                    error={registerForm.errors.email} label="email address" type="text" placeholder="Email address"
                    className="w-full" />

                    <div className="flex items-end w-full">
                        <div className="flex-1">
                            <Input autoComplete="off" disabled={!state.bCodeSent || isBusy} name="code" label="Verification code"
                            value={codeForm.state.code} onChange={codeForm.setFromInput}
                            error={codeForm.errors.code} type="text" placeholder="Verification code"
                            className="w-full join-item" />
                        </div>
                        <div className="flex-none">
                            <button disabled={isBusy || state.timer > 0} onClick={sendCode} className="btn tooltip" data-tip="Send Code">{codeForm.loadText(state.bCodeSent ? <><MailCheck size={state.timer > 0 ? 16 : undefined} className="inline" /> {state.timer > 0 ? state.timer : ""}</> : <Mail />)}</button>
                        </div>
                    </div>

                    <div>
                        <button disabled={isBusy || isBlank(codeForm.state.code)} onClick={verifyCode} className="btn w-full btn-primary">{registerForm.loadText(<><ArrowRight size={16} /> Next</>)}</button>
                    </div>
                </div>
            )}
        </Modal>
    );
}